import React, { useRef, useCallback, useEffect, useState } from 'react';

/**
 * DualRangeSlider - A clean range slider with two draggable thumbs
 * Used for selecting min/max values in a range (like price filters, date ranges, etc.)
 */
export function DualRangeSlider({ min, max, value, onChange, formatLabel }) {
    const trackRef = useRef(null);
    const [dragging, setDragging] = useState(null); // 'start' | 'end' | null

    const [minValue, maxValue] = value;

    const getPercentage = (val) => {
        return ((val - min) / (max - min)) * 100;
    };

    const handleMouseDown = (thumb) => (e) => {
        e.preventDefault();
        setDragging(thumb);
    };

    const handleMouseMove = useCallback((e) => {
        if (!dragging || !trackRef.current) return;

        const rect = trackRef.current.getBoundingClientRect();
        const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
        const newValue = Math.round((percentage / 100) * (max - min) + min);

        if (dragging === 'start') {
            onChange([Math.min(newValue, maxValue), maxValue]);
        } else if (dragging === 'end') {
            onChange([minValue, Math.max(newValue, minValue)]);
        }
    }, [dragging, min, max, minValue, maxValue, onChange]);

    const handleMouseUp = useCallback(() => {
        setDragging(null);
    }, []);

    useEffect(() => {
        if (dragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [dragging, handleMouseMove, handleMouseUp]);

    const startPercentage = getPercentage(minValue);
    const endPercentage = getPercentage(maxValue);

    return (
        <div className="dual-range-slider w-full">
            {/* Track container */}
            <div 
                ref={trackRef}
                className="relative h-1 bg-gray-300 rounded-full cursor-pointer"
            >
                {/* Active range (between thumbs) */}
                <div
                    className="absolute h-full bg-blue-500 rounded-full"
                    style={{
                        left: `${startPercentage}%`,
                        right: `${100 - endPercentage}%`
                    }}
                />

                {/* Start thumb */}
                <div
                    className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-md cursor-grab active:cursor-grabbing transform -translate-y-1/2 -translate-x-1/2 top-1/2"
                    style={{ left: `${startPercentage}%` }}
                    onMouseDown={handleMouseDown('start')}
                />

                {/* End thumb */}
                <div
                    className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-md cursor-grab active:cursor-grabbing transform -translate-y-1/2 -translate-x-1/2 top-1/2"
                    style={{ left: `${endPercentage}%` }}
                    onMouseDown={handleMouseDown('end')}
                />
            </div>

            {/* Labels */}
            {formatLabel && (
                <div className="flex justify-between mt-2 text-xs text-gray-600">
                    <span>{formatLabel(minValue)}</span>
                    <span>{formatLabel(maxValue)}</span>
                </div>
            )}
        </div>
    );
}

export default DualRangeSlider;
